import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ExpirationDialogComponent } from '../delivery-detail/expiration-dialog/expiration-dialog.component';
import { AuthService } from '../shared/auth.service';
import { Catch } from '../shared/catch.model';
import { GroundfishDelivery } from '../shared/groundfish-delivery.model';
import { LocalStorageService } from '../shared/local-storage.service';
import { LookupDataService } from '../shared/lookup-data.service';
import { LookupItem } from '../shared/lookup-item.model';
import { ConvertHMSToLogbook, ConvertToLogbook, ConvertTrawlToLogbook } from '../shared/submission-data.model';
import { HMSDelivery } from '../shared/hms-delivery.model';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { TrawlDelivery } from '../shared/trawl-delivery.model';
import { FishTicket } from '../shared/delivery-fish-ticket.model';
import { Guid } from 'guid-typescript';

/**
 * From the current delivery, this page is the last one before submission.
 * At this point the delivery to submit and its sets have no errors.
 * The user can do a final review and submit.
 */
@Component({
    selector: 'app-summary-review',
    templateUrl: './summary-review.component.html',
    styleUrls: ['./summary-review.component.css'],
    animations: [
        trigger('detailExpand', [
            state('collapsed,void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class SummaryReviewComponent implements OnInit {

  @ViewChild('catchTable') table!: MatTable<Catch>;
  loaded: boolean = false;
  catchItems: Catch[] = [];
  catchTableColumns: string[] = ['Species', 'RetainedCount', 'RetainedWeight', 'ReleasedCount', 'ReleasedWeight'];
  fishTicketColumns: string[] = ['Buyer', 'Fish Ticket Number', 'Date'];
  unloadingColumns: string[] = ['Species', 'Pounds Landed', 'Landing Receipt'];
  columnsToDisplay: string[] = ['Species', 'Pounds Landed', 'Landing Receipt'];
  columnsToDisplayWithExpand: string[] = [...this.columnsToDisplay, 'expand'];
  expandedElement: any | null;
  dataSource: any[] = [];

  fishery: string = '';
  groundfishLanding!: GroundfishDelivery;
  hmsLanding!: HMSDelivery;
  trawlLanding!: TrawlDelivery;
  
  trawlDeliveryMethod: string = '';
  containsHarpoon: boolean = false;

  ticketData: FishTicket[] = [];

  finalSubmit: boolean = false;
  draftSubmit: boolean = false;
  isSubmitting: boolean = false;

  speciesOptions: LookupItem[] = [];
  speciesSubscription!: Subscription;

  previousScreen: string = 'menu';
  deliveryIndex: number = -1;
  draftIndex: number = -1;

  constructor(public router: Router,
    private route: ActivatedRoute,
    public lookup: LookupDataService,
    public dialog: MatDialog,
    public onlineStatusService: OnlineStatusService,
    public toastr: ToastrService,
    public _snackBar: MatSnackBar,
    public auth: AuthService) { }

  ngOnInit(): void {
    this.previousScreen = this.route.snapshot.queryParams.previous ?? 'menu';
    this.fishery = <string>this.route.snapshot.paramMap.get('fishery');
    if (this.fishery == 'Groundfish') this.initializeGroundfish();
    else if (this.fishery == 'HMS') this.initializeHMS();
    else if (this.fishery == 'Trawl') this.initializeTrawl();
  }

  initializeGroundfish() {
    this.groundfishLanding = new GroundfishDelivery();
    LocalStorageService.storage_promise.then(result => {
      this.deliveryIndex = +this.route.snapshot.queryParams.deliveryIndex;
      this.groundfishLanding = this.deliveryIndex != -1 ? LocalStorageService.edited_logbook.GF : LocalStorageService.user_trip.groundfishDelivery;
      this.ticketData = this.groundfishLanding.tickets;
      this.initializeTableGroundfish(this.groundfishLanding);
    });

    this.speciesSubscription = this.lookup.getSpeciesObservable('Groundfish').subscribe((value) => this.speciesOptions = value);
  }

  initializeHMS() {
    this.hmsLanding = new HMSDelivery();
    LocalStorageService.storage_promise.then(result => {
      this.deliveryIndex = +this.route.snapshot.queryParams.deliveryIndex;
      this.draftIndex = +(this.route.snapshot.queryParams.draftIndex ?? -1);
      this.hmsLanding = this.deliveryIndex != -1 ? LocalStorageService.edited_logbook.HMS : this.draftIndex != -1 ? LocalStorageService.hms_drafts[this.draftIndex] : LocalStorageService.user_trip.hmsDelivery;  
      this.containsHarpoon = this.hmsLanding.sets.some(st => st.gear.type == 'Harpoon');
      this.initializeTableHMS(this.hmsLanding);
      this.initializeTableGroundfish(this.hmsLanding);
    });
  }

  initializeTrawl() {
    this.trawlLanding = new TrawlDelivery();
    LocalStorageService.storage_promise.then(result => {
      this.deliveryIndex = +this.route.snapshot.queryParams.deliveryIndex;
      this.draftIndex = +(this.route.snapshot.queryParams.draftIndex ?? -1);
      this.trawlLanding = this.deliveryIndex != -1 ? LocalStorageService.edited_trawlLogbook : this.draftIndex != -1 ? LocalStorageService.trawl_drafts[this.draftIndex] : LocalStorageService.user_trawlTrip.trawlDelivery; 
      this.ticketData = this.trawlLanding.tickets; 
      this.initializeTableGroundfish(this.trawlLanding);
    });
    
    this.speciesSubscription = this.lookup.getSpeciesObservable('Trawl').subscribe((value) => this.speciesOptions = value);
  }

  initializeTableHMS(landing: HMSDelivery) {
    const flattenedData: any[] = [];
  
    // only loop if unloading is actually an array (can be undefined)
    (landing.unloading ?? []).forEach(unloading => {
      (unloading.species ?? []).forEach(species => {
        flattenedData.push({
          unloadingPort: unloading.unloadingPort?.name,
          unloadingDate: unloading.unloadingDate,
          speciesName: species.speciesName,
          poundsLanded: species.poundsLanded,
          landingReceipt: species.landingReceipt
        });
      });
    });
  
    this.dataSource = flattenedData;
  }


  initializeTableGroundfish(landing: GroundfishDelivery | HMSDelivery | TrawlDelivery) {
    let species = new Map<string, Catch>();
    landing.sets.forEach(set => {
      set.catchItems.forEach(cth => {
        //These need to be numbers but are being interpreted as strings?
        if (species.has(cth.species.name)) {
          let c = species.get(cth.species.name);
          if (c?.releasedCount) c!.releasedCount += cth.releasedCount ?? 0;
          if (c?.retainedCount) c!.retainedCount += cth.retainedCount ?? 0;
          if (c?.releasedWeight) c!.releasedWeight += cth.releasedWeight ?? 0;
          if (c?.retainedWeight) c!.retainedWeight += cth.retainedWeight ?? 0;
          species.set(c!.species.name, c!);
        } else {
          species.set(cth.species.name, Catch.transformAnonymous([cth])[0]);
        }
      });
    });

    species.forEach(pair => {
      this.catchItems.push(pair);
    });
    this.table.renderRows();
  }




  /**
   * User is submitting their logbook. Serialize their data and submit.
   * Check the access and refresh tokens before doing so.
   * Upon successful submission a sync is performed.
   * @returns 
   */
  submitToServer() {
    if (this.isSubmitting) return;
    if (this.onlineStatusService.getStatus() == OnlineStatusType.OFFLINE) {
      this.toastr.error('Cannot submit logbook while offline', 'Logbook Not Submitted')
      return;
    }

    let landing = this.fishery == 'Trawl' ? this.trawlLanding : this.fishery == 'HMS' ? this.hmsLanding : this.groundfishLanding;
    if (this.deliveryIndex != -1 && new Date(landing.receivedDate.valueOf() + 7*24*60*60*1000) < new Date()) {
      this.toastr.error('Edit time window for this delivery has passed', 'Logbook Not Submitted');
      return;
    }
    
    //Serialize the form to JSON
    let logbook = this.fishery == 'Groundfish' ? ConvertToLogbook(this.groundfishLanding, this.speciesOptions) : 
                  this.fishery == 'Trawl' ? ConvertTrawlToLogbook(this.trawlLanding, this.speciesOptions) : ConvertHMSToLogbook(this.hmsLanding);
    console.log(logbook);   
    this.isSubmitting = true;
  
    //Attempt to send to server
    let send = this._snackBar.open("Sending...");
    if (this.auth.checkAccessToken().expired) {
      console.log("Access token expired, refreshing..");
      if (this.auth.checkRefreshToken().expired) {
        //Log out!
        console.log("Refresh token expired!");
        this.isSubmitting = false;
        send.dismiss();
        this.onRefreshTokenExpiration();   
        return;      
      } else {
        //Refresh access token
        this.auth.postRefreshToken().subscribe(res => {
          LocalStorageService.current_user.refreshToken = res.refreshToken;
          LocalStorageService.current_user.accessToken = res.accessToken;
          LocalStorageService.accessToken = res.accessToken;
          LocalStorageService.updateUser(false, true);
          this.auth.postSubmitLogbook(logbook).subscribe(res => {
            console.log(res);
            if (res.receivedDateTime) {
              send.dismiss();
              this.isSubmitting = false;
              if (this.fishery == 'Groundfish') this.afterSubmitSuccessGroundfish(res.receivedDateTime, res.landingLogId)
              else if (this.fishery == 'Trawl') this.afterSubmitSuccessTrawl(res.receivedDateTime, res.landingLogId)
              else this.afterSubmitSuccessHMS(res.receivedDateTime, res.landingLogId);             
            } else {
              this.toastr.error(res, 'Logbook Not Submitted');
              this.isSubmitting = false;
            }
          }, (err: HttpErrorResponse) => {
            console.log(err)
            this.toastr.error(err.error, 'Logbook Not Submitted', {timeOut: 7000});
            this.isSubmitting = false;
          });
        }, err => {
          console.log(err)
            this.toastr.error('Refresh token error. Please log out and in again to submit.', 'Logbook Not Submitted', {timeOut: 7000});
            this.isSubmitting = false;
        });
      }
    } else {
      //All good! Send it off.
      this.auth.postSubmitLogbook(logbook).subscribe(res => {
        console.log(res);
        if (res.receivedDateTime) {
          send.dismiss();
          this.isSubmitting = false;
          if (this.fishery == 'Groundfish') this.afterSubmitSuccessGroundfish(res.receivedDateTime, res.landingLogId) 
          else if (this.fishery == 'Trawl') this.afterSubmitSuccessTrawl(res.receivedDateTime, res.landingLogId)
          else this.afterSubmitSuccessHMS(res.receivedDateTime, res.landingLogId);
        } else {
          this.toastr.error(res, 'Logbook Not Submitted');
          this.isSubmitting = false;
        }
      }, (err: HttpErrorResponse) => {
        console.log(err)
        this.toastr.error(err.error, 'Logbook Not Submitted', {timeOut: 7000});
        this.isSubmitting = false;
      });
    };
    send.dismiss();
  }
  

  /**
   * Refresh token expired. Open dialog stating to the user to re log in order to be able to submit
   */
  onRefreshTokenExpiration() {
    const dialogRef = this.dialog.open(ExpirationDialogComponent, {
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {  // only log the user out if they confirm
        LocalStorageService.logged_in = false;
        LocalStorageService.local_storage.get('login_status').then((doc: any) => {
          doc.user = '';
          doc.isLoggedIn = false;
          LocalStorageService.local_storage.put(doc);
          LocalStorageService.cleanupStorage();
          localStorage.setItem('rtokenexpiry', 'true');
          this.router.navigate(['login']);
        });
      } else {
        this.toastr.warning('Login again to submit your logbook', 'Cannot Submit Logbook');
      }
    });
  }


  /**
   * Logbook received. From here, {@link LocalStorageService.afterDeliveryReceived} does the rest.
   * @param receivedDate From server
   * @param id Also from server
   */
  afterSubmitSuccessGroundfish(receivedDate: Date, id: number) {
    let rc = new Date(receivedDate);
    rc.setHours(rc.getHours() - rc.getTimezoneOffset()/60);
    
    if (this.deliveryIndex == -1) {
      this.groundfishLanding.logbookId = "received-logbook-" + id;
      this.groundfishLanding.receivedDate = rc;

    }
    
    this.groundfishLanding.submissionDate = rc;
    ++this.groundfishLanding.logbookVersion;
    this._snackBar.open("Logbook Received - " + this.groundfishLanding.submissionDate, '', {duration: 5000});

    //Store delivery data and go to... menu?
    if (this.deliveryIndex != -1) LocalStorageService.afterLogbookResubmission(this.groundfishLanding).then(l => this.router.navigate(['menu']));
    else LocalStorageService.afterDeliveryReceived(this.groundfishLanding).then(l => this.router.navigate(['menu']));
  }

  afterSubmitSuccessHMS(receivedDate: Date, id: number) {
    let rc = new Date(receivedDate);
    rc.setHours(rc.getHours() - rc.getTimezoneOffset()/60);
    
    if (this.deliveryIndex == -1) {
      this.hmsLanding.logbookId = "received-logbook-" + id;
      this.hmsLanding.receivedDate = rc;

    }
    
    this.hmsLanding.submissionDate = rc;
    ++this.hmsLanding.logbookVersion;
    this._snackBar.open("Logbook Received - " + this.hmsLanding.submissionDate, '', {duration: 5000});

    //Store delivery data and go to... menu?
    if (this.deliveryIndex != -1) LocalStorageService.afterLogbookResubmission(this.hmsLanding).then(l => this.router.navigate(['menu']));
    else LocalStorageService.afterDeliveryReceived(this.hmsLanding, "default", this.draftIndex != -1).then(l => this.router.navigate(['menu']));
  }

/**
   * Logbook received. From here, {@link LocalStorageService.afterDeliveryReceived} does the rest.
   * @param receivedDate From server
   * @param id Also from server
   */
afterSubmitSuccessTrawl(receivedDate: Date, id: number) {
  let rc = new Date(receivedDate);
  rc.setHours(rc.getHours() - rc.getTimezoneOffset()/60);
  
  if (this.deliveryIndex == -1) {
    this.trawlLanding.logbookId = "received-logbook-" + id;
    this.trawlLanding.receivedDate = rc;

  }
  
  this.trawlLanding.submissionDate = rc;
  ++this.trawlLanding.logbookVersion;
  this._snackBar.open("Logbook Received - " + this.trawlLanding.submissionDate, '', {duration: 5000});

  //Store delivery data and go to... menu?
  if (this.deliveryIndex != -1) LocalStorageService.afterLogbookResubmission(this.trawlLanding, 'trawl').then(l => this.router.navigate(['menu']));
  else LocalStorageService.afterDeliveryReceived(this.trawlLanding, 'trawl', this.draftIndex != -1).then(l => this.router.navigate(['menu']));
}

  onSubmitFailure() {
    this.isSubmitting = false;
    this._snackBar.open("Submission failed, check connection and try again", 'dismiss', {duration: 3000});
  }

  saveDraft() {
    if (this.fishery == 'Groundfish') return; // button shouldn't be visible if fishery is groundfish but just in case.
    else if (this.fishery == 'Trawl') this.saveTrawlDraft();
    else if (this.fishery == "HMS") this.saveHMSDraft();
  }

  saveTrawlDraft() {
    if (!this.trawlLanding.localLandingGUID || this.trawlLanding.localLandingGUID == "") 
      this.trawlLanding.localLandingGUID = Guid.create().toString().toUpperCase();

    //Store draft and go to menu
    LocalStorageService.saveTrawlDraftTrip(this.trawlLanding).then(res => this.router.navigate(['menu']));
    this._snackBar.open("Trip saved to drafts", '', {duration: 5000});
  }

  saveHMSDraft() {
    if (!this.hmsLanding.localLandingGUID || this.hmsLanding.localLandingGUID == "") 
      this.hmsLanding.localLandingGUID = Guid.create().toString().toUpperCase();

    //Store draft and go to menu
    LocalStorageService.saveHMSDraftTrip(this.hmsLanding).then(res => this.router.navigate(['menu']));
    this._snackBar.open("Trip saved to drafts", '', {duration: 5000});
  }

  navigateBack() {
    const route = this.fishery == 'Trawl' ? 'trawl_delivery' : this.fishery == 'Groundfish' ? 'delivery_detail' : 'hms_delivery_detail';
    this.router.navigate([route, this.draftIndex != -1 ? 3 : this.deliveryIndex == -1 ? 0 : 2, this.draftIndex != -1 ? this.draftIndex : this.deliveryIndex], { queryParams: { previous: this.previousScreen } });   
  }

}
